// SwipeDeck Component
// Main container for the swipe deck with card management

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hand, ArrowLeft, ArrowRight } from 'lucide-react';
import { SwipeCard, SwipeDirection, SwipeAction, UserProfile, SwipeAllowance } from '../../../types/swipe';
import { SwipeCardStack } from '../SwipeCardStack';
import { swipeService } from '../../../services/Swipe/swipeService';
import { PremiumModal } from '../../../components/Premium';
import {
  SwipeDeckContainer,
  SwipeDeckHeader,
  SwipeDeckTitle,
  SwipeDeckStats,
  StatItem,
  StatValue,
  StatLabel,
  SwipeDeckContent,
  SwipeDeckFooter,
  RefreshButton,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateMessage,
  LoadingOverlay,
  LoadingSpinner,
  LoadingText,
  MobileInstructionText,
  InstructionIcon,
  InstructionLabel
} from '../../../styles/Swipe/SwipeDeck.styles';
import { logger } from '../../../utils/logger';
import { DISCOVERY_SECTION_ID } from '../../../constants/settingsAnchors';
import { useSubscriptionContext } from '../../../providers/SubscriptionProvider';

interface SwipeDeckProps {
  userId: string;
  onKeep: (card: SwipeCard) => void;
  onDiscard: (card: SwipeCard) => void;
  onSkip: (card: SwipeCard) => void;
  keepPileCount: number;
  maxKeepPileSize?: number;
  /** Today's swipe allowance, owned by SwipeHub. */
  allowance?: SwipeAllowance | null;
  /** A swipe was accepted by the server. */
  onSwipeSpent?: () => void;
  /** The server refused a swipe because the daily limit is spent. */
  onSwipeLimitReached?: () => void;
  onCardExhausted?: () => void;
  onShowNotification?: (type: 'match' | 'request' | 'info' | 'warning', message: string) => void;
}

export const SwipeDeck: React.FC<SwipeDeckProps> = ({
  userId,
  onKeep,
  onDiscard,
  onSkip,
  keepPileCount,
  maxKeepPileSize = 5,
  allowance = null,
  onSwipeSpent,
  onSwipeLimitReached,
  onCardExhausted,
  onShowNotification
}) => {
  const [cards, setCards] = useState<SwipeCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [stats, setStats] = useState({
    cardsViewed: 0,
    cardsRemaining: 0,
  });
  const navigate = useNavigate();
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);
  const { isPremium, isPremiumPlus } = useSubscriptionContext();

  // The allowance is OWNED BY SwipeHub, not fetched here. The header shows it
  // and the deck spends it, so two independent copies would drift apart the
  // first time one updated without the other.
  const limitReached = !!allowance && !allowance.unlimited && (allowance.remaining ?? 0) <= 0;

  // Load initial cards
  const loadCards = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const newCards = await swipeService.getCards(userId, 10);

      setCards(newCards);
      // THE INDEX HAS TO GO BACK TO THE START OF THE NEW DECK.
      //
      // This replaced `cards` and left currentIndex where it was. The deck is
      // fetched ten at a time, so after ten swipes currentIndex was 10, the
      // "you're all caught up" branch fired, and handleCardExhausted quietly
      // reloaded ten fresh cards underneath it — into an array the index was
      // still pointing past the end of. 10 >= 10 stayed true forever.
      //
      // The effect was a deck that emptied permanently after ten swipes,
      // whatever the tier and however many candidates were left. Lamont had
      // thirteen people waiting behind that screen.
      setCurrentIndex(0);
      setStats(prev => ({
        ...prev,
        cardsRemaining: newCards.length
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cards');
      console.error('Error loading cards:', err);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Load cards on mount
  useEffect(() => {
    loadCards();
  }, [loadCards]);

  // Handle swipe actions
  const handleSwipe = async (cardId: string, direction: SwipeDirection) => {
    const card = cards.find(c => c.id === cardId);
    if (!card) return;

    logger.log('🃏 Swipe direction:', direction, 'Card:', card.teaser.name);

    // Check if keep pile is full BEFORE setting animation state
    // This prevents the card from getting stuck in animating state
    const isKeepAction = direction === 'right' || direction === 'down';
    if (isKeepAction && keepPileCount >= maxKeepPileSize) {
      // Show error - keep pile full
      if (onShowNotification) {
        onShowNotification('warning', 'Keep pile is full. Review your cards before adding more.');
      }
      return; // Return early without setting animation state
    }

    // Mark card as swiped immediately to trigger animation
    // Don't update currentIndex yet - let the card animate away first
    setCards(prevCards =>
      prevCards.map(c =>
        c.id === cardId
          ? { ...c, swipeDirection: direction, isAnimating: true }
          : c
      )
    );

    // Map direction to action
    let action: SwipeAction;
    switch (direction) {
      case 'left':
        action = 'DISCARD';
        onDiscard(card);
        break;
      case 'right':
        // Right swipe (not used in poker - could be for quick keep)
        action = 'KEEP';
        onKeep(card);
        break;
      case 'down':
        // Down swipe or "Keep" button - add to keep pile
        action = 'KEEP';
        onKeep(card);
        break;
      case 'up':
        action = 'SKIP';
        onSkip(card);
        break;
      default:
        return;
    }

    // THE SWIPE NO LONGER WAITS ON THE NETWORK.
    //
    // This used to `await recordSwipe(...)` and only then start the 300ms
    // timer, so the card flew off, sat there for a round trip, and only then
    // did the next card appear. The visible delay was latency + 300ms, which
    // is the hesitation between cards. The card's own CSS animation had
    // finished long before.
    //
    // The write is fired and handled below; the deck advances on the animation
    // alone. Recording a swipe is not something the next card depends on.
    const recording = swipeService.recordSwipe(userId, cardId, action);

    recording
      .then(() => {
        // Spend a swipe so the counter moves with the gesture rather than a
        // round trip later. The server stays the authority: the count is
        // re-read on mount and on tier change, and corrected the moment a
        // swipe is refused.
        onSwipeSpent?.();
      })
      .catch((err: any) => {
        console.error('Error recording swipe:', err);
        if (err?.code === 'SWIPE_LIMIT_REACHED' || err?.status === 403) {
          // Out of swipes: stop the deck and say so. This used to be swallowed
          // into console.error, so swiping past the limit silently did nothing
          // at all — the cards kept moving and none of them counted.
          onSwipeLimitReached?.();
          if (onShowNotification) {
            onShowNotification('warning', err?.message || 'You have used all of today\'s swipes.');
          }
        } else if (onShowNotification) {
          onShowNotification('warning', 'That swipe could not be saved. Please try again.');
        }
      });

    {
      // Update stats
      setStats(prev => ({
        ...prev,
        cardsViewed: prev.cardsViewed + 1,
        cardsRemaining: Math.max(0, prev.cardsRemaining - 1),
      }));

      // Wait for animation to complete before moving to next card
      const animationDuration = 300; // Match the swipe animation duration
      setTimeout(() => {
        // Move to next card - this removes the swiped card from visible range
        const newIndex = currentIndex + 1;
        setCurrentIndex(newIndex);

        // Clear swipeDirection from the swiped card and reset next card state
        // Do this in the same update to avoid flicker
        setCards(prevCards => {
          return prevCards.map((card, index) => {
            // Clear swipeDirection from the card that was just swiped (at old currentIndex)
            if (index === currentIndex) {
              return { ...card, swipeDirection: undefined, isAnimating: false };
            }
            // Reset flip state for the next card to ensure it starts unflipped
            if (index === newIndex && newIndex < prevCards.length) {
              return { ...card, isFlipped: false, swipeDirection: undefined, isAnimating: false };
            }
            return card;
          });
        });
      }, animationDuration);
    }
  };

  // Handle card flip (memoized to prevent infinite loops)
  const handleFlip = useCallback(() => {
    // Always flip the current card (the one at currentIndex)
    const currentCard = cards[currentIndex];
    if (!currentCard) {
      logger.log('❌ No current card found at index:', currentIndex);
      return;
    }

    logger.log('🔄 Flip button clicked for card:', currentCard.id, currentCard.teaser.name, 'at index:', currentIndex);
    setCards(prevCards => {
      const updatedCards = prevCards.map(card =>
        card.id === currentCard.id
          ? { ...card, isFlipped: !card.isFlipped }
          : card
      );
      logger.log('🔄 Cards after flip:', updatedCards.map(c => ({ id: c.id, name: c.teaser.name, isFlipped: c.isFlipped })));
      return updatedCards;
    });
  }, [cards, currentIndex]);

  // Handle cards exhausted
  const handleCardExhausted = useCallback(() => {
    if (onCardExhausted) {
      onCardExhausted();
    }
    // Auto-load more cards
    loadCards();
  }, [onCardExhausted, loadCards]);

  // Refresh deck. loadCards resets the index itself now, but this is kept
  // explicit: the button's promise is "start again", and relying on a side
  // effect of the fetch for that is how it gets lost in a later edit.
  const handleRefresh = () => {
    setCurrentIndex(0);
    loadCards();
  };

  // Check if keep pile is full
  const isKeepPileFull = keepPileCount >= maxKeepPileSize;

  // Check if device is mobile
  const isMobile = useMemo(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (typeof window !== 'undefined' && window.innerWidth <= 768);
  }, []);

  // Render loading state
  if (isLoading && cards.length === 0) {
    return (
      <SwipeDeckContainer>
        <LoadingOverlay>
          <LoadingSpinner />
          <LoadingText>Finding your matches...</LoadingText>
        </LoadingOverlay>
      </SwipeDeckContainer>
    );
  }

  // Render error state
  if (error && cards.length === 0) {
    return (
      <SwipeDeckContainer>
        <EmptyState>
          <EmptyStateIcon>⚠️</EmptyStateIcon>
          <EmptyStateTitle>Oops! Something went wrong</EmptyStateTitle>
          <EmptyStateMessage>{error}</EmptyStateMessage>
          <RefreshButton onClick={handleRefresh}>
            Try Again
          </RefreshButton>
        </EmptyState>
      </SwipeDeckContainer>
    );
  }

  // Two different endings, told apart at last.
  //
  // This screen said "Out of Daily Swipes! You've hit your daily limit.
  // Upgrade to Premium" whenever the deck simply ran out of CARDS — which has
  // nothing to do with the limit. Someone who had swiped three times and
  // exhausted the candidate pool was told they were out of swipes and sold an
  // upgrade that would not have produced a single extra card. It also said it
  // to Premium Plus subscribers, who have unlimited swipes already.
  if (limitReached) {
    const resetCopy = 'Your swipes reset at midnight.';
    return (
      <SwipeDeckContainer>
        <EmptyState>
          <EmptyStateIcon>👑</EmptyStateIcon>
          <EmptyStateTitle>Out of swipes for today</EmptyStateTitle>
          <EmptyStateMessage>
            {allowance?.dailyLimit
              ? `You have used all ${allowance.dailyLimit} of today's swipes. ${resetCopy}`
              : `You have used all of today's swipes. ${resetCopy}`}
            {!isPremiumPlus && ' Premium Plus gets unlimited swipes.'}
          </EmptyStateMessage>
          {!isPremiumPlus && (
            <RefreshButton onClick={() => setIsPremiumModalOpen(true)}>
              {isPremium ? 'Upgrade to Premium Plus' : 'See plans'}
            </RefreshButton>
          )}
        </EmptyState>
        <PremiumModal
          isOpen={isPremiumModalOpen}
          onClose={() => setIsPremiumModalOpen(false)}
        />
      </SwipeDeckContainer>
    );
  }

  // Genuinely out of cards — a discovery problem, not a billing one.
  if (cards.length === 0 || currentIndex >= cards.length) {
    return (
      <SwipeDeckContainer>
        <EmptyState>
          <EmptyStateIcon>🃏</EmptyStateIcon>
          <EmptyStateTitle>You're all caught up</EmptyStateTitle>
          <EmptyStateMessage>
            You have seen everyone who matches your preferences. Widening your
            age range or the genders you want to see is usually the quickest way
            to find more people.
          </EmptyStateMessage>
          {/* Now that preferences are editable outside onboarding, this can
              offer the actual fix instead of only naming it. Running out of
              cards is nearly always a filter that is too narrow, not an empty
              platform — and the difference is invisible from here. */}
          {/* Says where it came from, so Settings' Back returns to the deck
              rather than to the profile page nobody was on. */}
          <RefreshButton
            onClick={() =>
              navigate(`/settings#${DISCOVERY_SECTION_ID}`, { state: { from: '/swipe' } })
            }
          >
            Adjust who you see
          </RefreshButton>
          <RefreshButton onClick={loadCards} style={{ background: 'transparent' }}>
            Check again
          </RefreshButton>
        </EmptyState>
        <PremiumModal
          isOpen={isPremiumModalOpen}
          onClose={() => setIsPremiumModalOpen(false)}
        />
      </SwipeDeckContainer>
    );
  }

  // Get current card
  const currentCard = cards[currentIndex];

  logger.log('🎴 SwipeDeck render:', {
    totalCards: cards.length,
    currentIndex,
    currentCard: currentCard ? { id: currentCard.id, name: currentCard.teaser.name, isFlipped: currentCard.isFlipped } : null
  });

  // Render deck - Simplified for High Stakes Poker (no header/footer clutter)
  return (
    <SwipeDeckContainer>
      {/* Card stack - Clean, full-screen experience */}
      <SwipeDeckContent>
        <SwipeCardStack
          cards={cards} // Pass all cards
          currentIndex={currentIndex} // Pass current index
          onSwipe={handleSwipe}
          onCardFlip={(cardId) => {
            // Only flip if this is the current card
            if (currentCard && currentCard.id === cardId) {
              handleFlip();
            }
          }}
          onCardExhausted={handleCardExhausted}
          isLoading={isLoading}
          error={error}
        />
      </SwipeDeckContent>

      {/* Instruction icons - positioned below card stack */}
      {currentCard && (
        <MobileInstructionText>
          <InstructionIcon>
            <ArrowLeft size={16} />
          </InstructionIcon>
          <InstructionLabel>Pass</InstructionLabel>
          <InstructionIcon>
            <Hand size={16} />
          </InstructionIcon>
          <InstructionLabel>Tap to flip</InstructionLabel>
          <InstructionLabel>Keep</InstructionLabel>
          <InstructionIcon>
            <ArrowRight size={16} />
          </InstructionIcon>
        </MobileInstructionText>
      )}

      {/* Action Buttons - Inside container but below cards (hidden on mobile) */}
      {currentCard && !isMobile && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0.5rem',
          marginTop: '1rem',
          paddingBottom: '0.5rem',
          paddingLeft: '0.5rem',
          paddingRight: '0.5rem',
          flexWrap: 'nowrap'
        }}>
          {/* Check User / Show Video Button */}
          <button
            onClick={() => {
              logger.log('🔘 Button clicked for current card:', currentCard?.id, currentCard?.teaser.name, 'at index:', currentIndex);
              logger.log('🔘 All cards:', cards.map(c => ({ id: c.id, name: c.teaser.name, isFlipped: c.isFlipped })));
              handleFlip();
            }}
            style={{
              padding: '0.75rem 1rem',
              fontSize: '0.9rem',
              fontWeight: 600,
              border: 'none',
              borderRadius: '25px',
              background: currentCard.isFlipped
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              cursor: 'pointer',
              boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              minWidth: '140px',
              justifyContent: 'center',
              flex: '1 1 0',
              maxWidth: '180px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
            }}
          >
            <span style={{ fontSize: '1rem' }}>
              {currentCard.isFlipped ? '🎬' : '🔍'}
            </span>
            {currentCard.isFlipped ? 'Show Video' : 'Check User'}
          </button>

          {/* Deal Button - Pass on current card */}
          <button
            onClick={() => {
              logger.log('🃏 Deal button clicked - passing on card:', currentCard?.id, currentCard?.teaser.name);
              handleSwipe(currentCard.id, 'left'); // Pass/Discard the card
            }}
            style={{
              padding: '0.75rem 1rem',
              fontSize: '0.9rem',
              fontWeight: 600,
              border: 'none',
              borderRadius: '25px',
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
              color: 'white',
              cursor: 'pointer',
              boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              minWidth: '140px',
              justifyContent: 'center',
              flex: '1 1 0',
              maxWidth: '180px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
            }}
          >
            <span style={{ fontSize: '1rem' }}>🃏</span>
            Deal
          </button>
        </div>
      )}
    </SwipeDeckContainer>
  );
};

